import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Download, Edit2, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Button } from '../components/Button';
import { CONTACT_INFO } from '../constants/contact';

interface Goal {
  id: string;
  name: string;
  corpusRequired: number;
  timeline: number;
  inflationRate: number;
  annualReturns: number;
}

interface CustomerInfo {
  name: string;
}

interface SchemeAllocation {
  id: string;
  goalId: string;
  schemeName: string;
  weightage: number;
  requiredAmount: number;
  proposedSIP: number;
}

const GoalPlanner: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);

  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: '',
  });

  const [goals, setGoals] = useState<Goal[]>([]);

  const [editingCell, setEditingCell] = useState<{
    goalId: string;
    field: keyof Goal;
  } | null>(null);

  const [editingCustomerField, setEditingCustomerField] = useState<
    keyof CustomerInfo | null
  >(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [createPlan, setCreatePlan] = useState(false);
  const [schemeAllocations, setSchemeAllocations] = useState<
    SchemeAllocation[]
  >([]);
  const plannerRef = useRef<HTMLDivElement>(null);

  // Initialize from URL on mount
  useEffect(() => {
    document.title = 'Goal Planner | NiveshRight';

    const searchParams = new URLSearchParams(location.search);
    const urlData = searchParams.get('data');

    if (urlData) {
      try {
        const decoded = JSON.parse(decodeURIComponent(urlData));

        if (decoded.customerInfo) {
          setCustomerInfo(decoded.customerInfo);
          if (decoded.customerInfo.name) {
            setIsSaved(true);
          }
        }

        if (
          decoded.goals &&
          Array.isArray(decoded.goals) &&
          decoded.goals.length > 0
        ) {
          setGoals(decoded.goals);
        }

        if (decoded.createPlan !== undefined) {
          setCreatePlan(decoded.createPlan);
        }

        if (
          decoded.schemeAllocations &&
          Array.isArray(decoded.schemeAllocations)
        ) {
          setSchemeAllocations(decoded.schemeAllocations);
        }
      } catch (error) {
        console.error('Error parsing URL data:', error);
      }
    }

    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update URL whenever state changes
  useEffect(() => {
    if (!isInitialized) return;

    const data = {
      customerInfo,
      goals,
      createPlan,
      schemeAllocations,
    };

    const encoded = encodeURIComponent(JSON.stringify(data));
    const searchParams = new URLSearchParams();
    searchParams.set('data', encoded);

    navigate(`?${searchParams.toString()}`, { replace: true });
  }, [
    customerInfo,
    goals,
    createPlan,
    schemeAllocations,
    isInitialized,
    navigate,
  ]);

  const addGoal = () => {
    const newGoal: Goal = {
      id: Date.now().toString(),
      name: 'New Goal',
      corpusRequired: 0,
      timeline: 0,
      inflationRate: 6,
      annualReturns: 12,
    };
    setGoals([...goals, newGoal]);
  };

  const removeGoal = (id: string) => {
    setGoals(goals.filter(goal => goal.id !== id));
  };

  const startEditing = (goalId: string, field: keyof Goal, value: any) => {
    setEditingCell({ goalId, field });
    setEditValue(value.toString());
  };

  const saveEdit = () => {
    if (!editingCell) return;

    setGoals(
      goals.map(goal => {
        if (goal.id === editingCell.goalId) {
          const { field } = editingCell;
          let value: any = editValue;

          if (field !== 'name') {
            value = parseFloat(editValue) || 0;
          }

          return { ...goal, [field]: value };
        }
        return goal;
      })
    );

    setEditingCell(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const startEditingCustomer = (field: keyof CustomerInfo) => {
    setEditingCustomerField(field);
    setEditValue(customerInfo[field]);
  };

  const saveCustomerEdit = () => {
    if (!editingCustomerField) return;
    setCustomerInfo({ ...customerInfo, [editingCustomerField]: editValue });
    setEditingCustomerField(null);
    setEditValue('');
    if (editingCustomerField === 'name' && editValue.trim()) {
      setIsSaved(true);
    }
  };

  const cancelCustomerEdit = () => {
    setEditingCustomerField(null);
    setEditValue('');
  };

  const renderCustomerField = () => {
    const isEditing = editingCustomerField === 'name';
    const value = customerInfo.name;

    if (isEditing) {
      return (
        <input
          type="text"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={saveCustomerEdit}
          onKeyDown={e => {
            if (e.key === 'Enter') saveCustomerEdit();
            if (e.key === 'Escape') cancelCustomerEdit();
          }}
          className="w-full px-3 py-2 border-none rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Click to enter name"
          autoFocus
        />
      );
    }

    return (
      <div
        onClick={() => startEditingCustomer('name')}
        className="cursor-pointer hover:bg-blue-50 py-2 rounded-lg border-none group flex items-center justify-between min-h-[42px]"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>
          {value || 'Click to enter name'}
        </span>
        <Edit2
          size={14}
          className="opacity-0 group-hover:opacity-100 ml-2 text-gray-400"
        />
      </div>
    );
  };

  // Calculate required corpus in future considering inflation
  const calculateFutureCorpus = (
    presentCorpus: number,
    inflationRate: number,
    years: number
  ): number => {
    return presentCorpus * Math.pow(1 + inflationRate / 100, years);
  };

  // Calculate monthly SIP required
  const calculateMonthlySIP = (
    futureValue: number,
    annualReturns: number,
    years: number
  ): number => {
    const monthlyRate = annualReturns / 12 / 100;
    const months = years * 12;
    if (monthlyRate === 0) return futureValue / months;
    return (
      (futureValue * monthlyRate) / (Math.pow(1 + monthlyRate, months) - 1)
    );
  };

  // Calculate total invested amount
  const calculateTotalInvested = (
    monthlySIP: number,
    years: number
  ): number => {
    return monthlySIP * years * 12;
  };

  // Calculate growth
  const calculateGrowth = (
    futureCorpus: number,
    totalInvested: number
  ): number => {
    return futureCorpus - totalInvested;
  };

  // Calculate absolute returns percentage
  const calculateAbsoluteReturns = (
    growth: number,
    totalInvested: number
  ): number => {
    if (totalInvested === 0) return 0;
    return (growth / totalInvested) * 100;
  };

  const formatCurrency = (num: number): string => {
    if (isNaN(num) || !isFinite(num)) return '0';
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0,
    }).format(Math.round(num));
  };

  const handleCreatePlanToggle = (checked: boolean) => {
    setCreatePlan(checked);
    if (checked && schemeAllocations.length === 0) {
      // Initialize with one scheme per goal
      const initialAllocations: SchemeAllocation[] = goals.map(goal => {
        const futureCorpus = calculateFutureCorpus(
          goal.corpusRequired,
          goal.inflationRate,
          goal.timeline
        );
        const monthlySIP = calculateMonthlySIP(
          futureCorpus,
          goal.annualReturns,
          goal.timeline
        );

        return {
          id: `${goal.id}-${Date.now()}`,
          goalId: goal.id,
          schemeName: '',
          weightage: 100,
          requiredAmount: Math.round(monthlySIP),
          proposedSIP: Math.round(monthlySIP),
        };
      });
      setSchemeAllocations(initialAllocations);
    }
  };

  const addScheme = (goalId: string) => {
    const newScheme: SchemeAllocation = {
      id: `${goalId}-${Date.now()}`,
      goalId,
      schemeName: '',
      weightage: 0,
      requiredAmount: 0,
      proposedSIP: 0,
    };
    setSchemeAllocations([...schemeAllocations, newScheme]);
  };

  const removeScheme = (id: string) => {
    setSchemeAllocations(schemeAllocations.filter(s => s.id !== id));
  };

  const updateScheme = (
    id: string,
    field: keyof SchemeAllocation,
    value: any
  ) => {
    setSchemeAllocations(
      schemeAllocations.map(scheme => {
        if (scheme.id === id) {
          const updated = { ...scheme, [field]: value };

          // Recalculate requiredAmount based on weightage
          if (field === 'weightage') {
            const goal = goals.find(g => g.id === scheme.goalId);
            if (goal) {
              const futureCorpus = calculateFutureCorpus(
                goal.corpusRequired,
                goal.inflationRate,
                goal.timeline
              );
              const totalMonthlySIP = calculateMonthlySIP(
                futureCorpus,
                goal.annualReturns,
                goal.timeline
              );
              updated.requiredAmount = Math.round(
                (totalMonthlySIP * parseFloat(value)) / 100
              );
            }
          }

          return updated;
        }
        return scheme;
      })
    );
  };

  const getTotalWeightage = (goalId: string): number => {
    return schemeAllocations
      .filter(s => s.goalId === goalId)
      .reduce((sum, s) => sum + (s.weightage || 0), 0);
  };

  const exportToPDF = async () => {
    if (!plannerRef.current) return;
    if (!customerInfo.name.trim()) {
      alert('Please enter customer name before exporting');
      return;
    }

    setIsExporting(true);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();

      // Group goals into chunks of 4
      const goalsPerPage = 4;
      const goalChunks = [];
      for (let i = 0; i < goals.length; i += goalsPerPage) {
        goalChunks.push(goals.slice(i, i + goalsPerPage));
      }

      // Render each chunk on a separate page
      for (let chunkIndex = 0; chunkIndex < goalChunks.length; chunkIndex++) {
        const element = plannerRef.current;
        const clone = element.cloneNode(true) as HTMLElement;

        // Hide action buttons and labels
        const buttons = clone.querySelectorAll('.no-export');
        buttons.forEach(btn => {
          (btn as HTMLElement).style.display = 'none';
        });

        // Hide investor name section (we'll show it in the heading)
        const investorSection = clone.querySelector('.mb-6');
        if (
          investorSection &&
          investorSection.querySelector('.pdf-investor-name')
        ) {
          (investorSection as HTMLElement).style.display = 'none';
        }

        // Update Goal Assessment heading
        const goalHeading = clone.querySelector('.pdf-goal-heading');
        if (goalHeading) {
          (goalHeading as HTMLElement).style.display = 'block';
          (goalHeading as HTMLElement).style.fontSize = '20px';
          (goalHeading as HTMLElement).style.fontWeight = '400';
          (goalHeading as HTMLElement).style.marginBottom = '24px';
          (goalHeading as HTMLElement).style.color = '#1f2937';
          (goalHeading as HTMLElement).innerHTML =
            `Goal assessment for <span style="font-weight: 700; color: #111827;">${customerInfo.name}</span>`;
        }

        // Hide the plan allocation section on goal assessment pages
        const planAllocationSection = clone.querySelector(
          '.pdf-plan-allocation-section'
        );
        if (planAllocationSection) {
          (planAllocationSection as HTMLElement).style.display = 'none';
        }

        // Hide all goals except the current chunk
        const goalCards = clone.querySelectorAll(
          '.pdf-goal-cards-container > div'
        );
        goalCards.forEach((card, index) => {
          const chunkStart = chunkIndex * goalsPerPage;
          const chunkEnd = chunkStart + goalsPerPage;
          if (index < chunkStart || index >= chunkEnd) {
            (card as HTMLElement).style.display = 'none';
          }
        });

        // Hide total summary on all pages except the last one
        const totalSummary = clone.querySelector('.pdf-total-summary');
        if (totalSummary && chunkIndex < goalChunks.length - 1) {
          (totalSummary as HTMLElement).style.display = 'none';
        }

        // Style the clone
        clone.style.position = 'absolute';
        clone.style.left = '-9999px';
        clone.style.width = '1000px';
        clone.style.padding = '40px';
        clone.style.backgroundColor = '#ffffff';
        clone.style.boxShadow = 'none';
        clone.style.borderRadius = '0';

        // Better spacing for PDF
        const allElements = clone.querySelectorAll('*');
        allElements.forEach(el => {
          const element = el as HTMLElement;
          if (element.classList.contains('p-4')) {
            element.style.paddingBottom = '32px';
          }
          if (element.classList.contains('p-3')) {
            element.style.paddingBottom = '24px';
          }
          if (
            element.classList.contains('text-lg') ||
            element.classList.contains('text-xl') ||
            element.classList.contains('text-2xl')
          ) {
            element.style.lineHeight = '1.4';
          }
        });

        document.body.appendChild(clone);

        // Convert to canvas
        const canvas = await html2canvas(clone, {
          useCORS: true,
          logging: false,
          width: 1000,
        });

        document.body.removeChild(clone);

        const imgWidth = pdfWidth - 20;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const imgData = canvas.toDataURL('image/jpeg', 0.85);

        // Add new page if not the first chunk
        if (chunkIndex > 0) {
          pdf.addPage();
        }

        // Add image to PDF
        pdf.addImage(imgData, 'JPEG', 10, 10, imgWidth, imgHeight);
      }

      // Export plan allocation table on a separate page if it exists
      if (createPlan && schemeAllocations.length > 0) {
        // Add new page for plan allocation
        pdf.addPage();

        const element = plannerRef.current;

        // Render branding header as image
        const headerClone = element.cloneNode(true) as HTMLElement;
        const headerBranding = headerClone.querySelector(
          '.flex.justify-between.items-start.mb-4.pb-4.border-b'
        ) as HTMLElement;

        if (headerBranding) {
          // Create a wrapper just for the header
          const headerWrapper = document.createElement('div');
          headerWrapper.style.position = 'absolute';
          headerWrapper.style.left = '-9999px';
          headerWrapper.style.width = '1000px';
          headerWrapper.style.padding = '40px 40px 20px';
          headerWrapper.style.backgroundColor = '#ffffff';
          headerWrapper.appendChild(headerBranding);

          document.body.appendChild(headerWrapper);

          const headerCanvas = await html2canvas(headerWrapper, {
            useCORS: true,
            logging: false,
            width: 1000,
          });

          document.body.removeChild(headerWrapper);

          const headerImgWidth = pdfWidth - 20;
          const headerImgHeight =
            (headerCanvas.height * headerImgWidth) / headerCanvas.width;
          const headerImgData = headerCanvas.toDataURL('image/jpeg', 0.85);

          pdf.addImage(
            headerImgData,
            'JPEG',
            10,
            10,
            headerImgWidth,
            headerImgHeight
          );

          // Add heading text to match "Goal assessment for..." styling
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(31, 41, 55);
          pdf.text('Plan allocation for ', 15, headerImgHeight + 12);

          // Add bold name
          const textWidth = pdf.getTextWidth('Plan allocation for ');
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(17, 24, 39);
          pdf.text(customerInfo.name, 15 + textWidth, headerImgHeight + 12);

          // Build table data from scheme allocations
          const tableHeaders = [
            'Goal',
            'Required SIP',
            'Scheme Name',
            'Scheme Weightage',
            'Req. Scheme Amount',
            'Proposed Scheme SIP',
          ];

          const tableBody: any[] = [];
          const rowSpanInfo: any[] = [];

          goals.forEach(goal => {
            const goalSchemes = schemeAllocations.filter(
              s => s.goalId === goal.id
            );
            const futureCorpus = calculateFutureCorpus(
              goal.corpusRequired,
              goal.inflationRate,
              goal.timeline
            );
            const requiredSIP = calculateMonthlySIP(
              futureCorpus,
              goal.annualReturns,
              goal.timeline
            );

            const startRowIndex = tableBody.length;

            goalSchemes.forEach((scheme, index) => {
              tableBody.push([
                index === 0 ? goal.name : '',
                index === 0 ? Math.round(requiredSIP).toFixed(0) : '',
                scheme.schemeName,
                Math.round(scheme.weightage).toFixed(0),
                Math.round(scheme.requiredAmount).toFixed(0),
                Math.round(scheme.proposedSIP).toFixed(0),
              ]);
            });

            // Track rowspan for goal and required SIP columns
            if (goalSchemes.length > 1) {
              rowSpanInfo.push({
                startRow: startRowIndex,
                rowSpan: goalSchemes.length,
                goalName: goal.name,
                requiredSIP: Math.round(requiredSIP).toString(),
              });
            }
          });

          // Add total row
          const totalRequiredSIP = goals.reduce((sum, goal) => {
            const goalSchemes = schemeAllocations.filter(
              s => s.goalId === goal.id
            );
            if (goalSchemes.length === 0) return sum;
            const futureCorpus = calculateFutureCorpus(
              goal.corpusRequired,
              goal.inflationRate,
              goal.timeline
            );
            const requiredSIP = calculateMonthlySIP(
              futureCorpus,
              goal.annualReturns,
              goal.timeline
            );
            return sum + requiredSIP;
          }, 0);
          const totalRequiredAmount = schemeAllocations.reduce(
            (sum, scheme) => sum + scheme.requiredAmount,
            0
          );
          const totalProposedSIP = schemeAllocations.reduce(
            (sum, scheme) => sum + scheme.proposedSIP,
            0
          );
          tableBody.push([
            '',
            Math.round(totalRequiredSIP).toFixed(0),
            '',
            '',
            Math.round(totalRequiredAmount).toFixed(0),
            Math.round(totalProposedSIP).toFixed(0),
          ]);

          // Render table using autoTable
          autoTable(pdf, {
            head: [tableHeaders],
            body: tableBody,
            startY: headerImgHeight + 20,
            theme: 'grid',
            styles: {
              fontSize: 7,
              cellPadding: 2,
              lineColor: [209, 213, 219],
              lineWidth: 0.5,
            },
            headStyles: {
              fillColor: [0, 0, 0],
              textColor: [255, 255, 255],
              fontSize: 8,
              fontStyle: 'bold',
              halign: 'center',
            },
            alternateRowStyles: {
              fillColor: [249, 250, 251],
            },
            columnStyles: {
              0: { cellWidth: 35, fontStyle: 'bold' }, // Goal
              1: { cellWidth: 18, halign: 'right' }, // Required SIP - removed bold
              2: { cellWidth: 60 }, // Scheme Name
              3: { cellWidth: 18, halign: 'right' }, // Scheme Weightage - changed to right align
              4: { cellWidth: 22, halign: 'right' }, // Req. Scheme Amount
              5: { cellWidth: 22, halign: 'right' }, // Proposed Scheme SIP
            },
            didParseCell: function (data) {
              // Apply yellow background to header cells for columns 1, 3, 4, 5
              if (
                data.section === 'head' &&
                [1, 3, 4, 5].includes(data.column.index)
              ) {
                data.cell.styles.fillColor = [253, 224, 71]; // Yellow-300
                data.cell.styles.textColor = [0, 0, 0]; // Black text
              }

              // Style the total row (last row)
              if (
                data.section === 'body' &&
                data.row.index === tableBody.length - 1
              ) {
                data.cell.styles.fillColor = [0, 0, 0]; // Black
                data.cell.styles.textColor = [255, 255, 255]; // White text
                data.cell.styles.fontStyle = 'bold';
              }
            },
            didDrawCell: function (data) {
              // Draw borders to simulate rowspan for goals with multiple schemes
              if (data.section === 'body' && data.row.index >= 0) {
                const rowIndex = data.row.index;

                // Check if this row is part of a multi-scheme goal
                for (const spanInfo of rowSpanInfo) {
                  if (
                    rowIndex >= spanInfo.startRow &&
                    rowIndex < spanInfo.startRow + spanInfo.rowSpan
                  ) {
                    // For Goal and Required SIP columns, hide inner horizontal borders
                    if (data.column.index === 0 || data.column.index === 1) {
                      if (rowIndex > spanInfo.startRow) {
                        // Cover the top border with a white line
                        const doc = data.doc;
                        doc.setDrawColor(255, 255, 255);
                        doc.setLineWidth(1);
                        doc.line(
                          data.cell.x,
                          data.cell.y,
                          data.cell.x + data.cell.width,
                          data.cell.y
                        );
                      }
                    }
                    break;
                  }
                }
              }
            },
          });
        }
      }

      const fileName = `Goal_Planner_${customerInfo.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error exporting PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const renderCell = (goal: Goal, field: keyof Goal, value: any) => {
    const isEditing =
      editingCell?.goalId === goal.id && editingCell?.field === field;

    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <input
            type={field === 'name' ? 'text' : 'number'}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={e => {
              if (e.key === 'Enter') saveEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
            step={
              field === 'inflationRate' || field === 'annualReturns'
                ? '0.1'
                : '1'
            }
          />
        </div>
      );
    }

    return (
      <div
        className="cursor-pointer hover:bg-blue-50 py-1 rounded group flex items-center justify-between"
        onClick={() => startEditing(goal.id, field, value)}
      >
        <span>
          {field === 'name'
            ? value
            : field === 'inflationRate' || field === 'annualReturns'
              ? `${value}%`
              : formatCurrency(value)}
        </span>
        <Edit2 size={14} className="opacity-0 group-hover:opacity-100 ml-2" />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="w-full mx-auto">
        {/* Header with Export Button */}
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Goal Planner</h1>
          </div>
          <Button
            onClick={exportToPDF}
            disabled={isExporting || !isSaved}
            className="no-export"
          >
            {isExporting ? (
              <>Generating PDF...</>
            ) : (
              <>
                <Download size={18} />
              </>
            )}
          </Button>
        </div>

        {/* Main Content Card */}
        <div
          ref={plannerRef}
          className="bg-white rounded-lg shadow-lg p-4 w-full"
        >
          {/* Branding Header */}
          <div className="flex justify-between items-start mb-4 pb-4 border-b border-gray-200">
            <div>
              <img
                src="/logo-full-plus.svg"
                alt="NiveshRight"
                className="h-20"
              />
            </div>
            <div className="text-right text-sm text-gray-600">
              <p className="font-semibold">{CONTACT_INFO.phone.display}</p>
              <p>{CONTACT_INFO.email.general}</p>
              <p className="mt-1 text-gray-500">
                {new Date().toLocaleDateString('en-IN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>

          {/* Customer Information */}
          <div className="mb-6 max-w-md">
            <div className="flex items-center gap-2 mb-3 no-export">
              <label className="text-sm font-medium text-gray-700">
                Investor name
              </label>
            </div>
            <div className="pdf-investor-name">{renderCustomerField()}</div>
          </div>

          {/* Goals Cards */}
          <div className="mb-4 pdf-goal-assessment-section">
            {/* Heading - hidden in UI, shown in PDF with investor name */}
            <h3 className="hidden pdf-goal-heading text-lg font-semibold text-gray-900 mb-3">
              Goal assessment
            </h3>
            <div className="flex justify-end items-center mb-3 no-export">
              <Button onClick={addGoal}>
                <Plus size={18} />
              </Button>
            </div>

            <div className="space-y-4 pdf-goal-cards-container">
              {goals.map((goal, index) => {
                const futureCorpus = calculateFutureCorpus(
                  goal.corpusRequired,
                  goal.inflationRate,
                  goal.timeline
                );
                const monthlySIP = calculateMonthlySIP(
                  futureCorpus,
                  goal.annualReturns,
                  goal.timeline
                );
                const totalInvested = calculateTotalInvested(
                  monthlySIP,
                  goal.timeline
                );
                const growth = calculateGrowth(futureCorpus, totalInvested);
                const absoluteReturns = calculateAbsoluteReturns(
                  growth,
                  totalInvested
                );

                const borderColors = [
                  'border-l-blue-500',
                  'border-l-purple-500',
                  'border-l-green-500',
                  'border-l-orange-500',
                  'border-l-pink-500',
                  'border-l-indigo-500',
                  'border-l-teal-500',
                  'border-l-red-500',
                ];
                const borderColor = borderColors[index % borderColors.length];

                return (
                  <div
                    key={goal.id}
                    className={`bg-white rounded-lg p-4 relative shadow-sm border-l-4 ${borderColor}`}
                  >
                    {/* Goal Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="max-w-xlg">
                        <div className="text-2xl font-bold text-gray-900">
                          {renderCell(goal, 'name', goal.name)}
                        </div>
                      </div>
                      <button
                        onClick={() => removeGoal(goal.id)}
                        className="text-red-500 hover:text-red-700 transition-colors no-export"
                        title="Remove goal"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>

                    {/* Input Parameters */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                      <div className="flex flex-col">
                        <div className="text-xs font-semibold text-gray-500 mb-2 uppercase h-8 flex items-center">
                          Corpus Required Today
                        </div>
                        <div className="text-lg font-semibold text-gray-900">
                          {renderCell(
                            goal,
                            'corpusRequired',
                            goal.corpusRequired
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <div className="text-xs font-semibold text-gray-500 mb-2 uppercase h-8 flex items-center">
                          Timeline (in years)
                        </div>
                        <div className="text-lg font-semibold text-gray-900">
                          {renderCell(goal, 'timeline', goal.timeline)}
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <div className="text-xs font-semibold text-gray-500 mb-2 uppercase h-8 flex items-center">
                          Expected Inflation Rate
                        </div>
                        <div className="text-lg font-semibold text-gray-900">
                          {renderCell(
                            goal,
                            'inflationRate',
                            goal.inflationRate
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <div className="text-xs font-semibold text-gray-500 mb-2 uppercase h-8 flex items-center">
                          Expected Annual Returns
                        </div>
                        <div className="text-lg font-semibold text-gray-900">
                          {renderCell(
                            goal,
                            'annualReturns',
                            goal.annualReturns
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Calculated Results */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div className="bg-gray-100 rounded-lg p-3 flex flex-col">
                        <div className="text-xs font-semibold text-gray-700 mb-2 uppercase h-8 flex items-center">
                          Required Corpus (in future)
                        </div>
                        <div className="text-lg font-bold text-gray-900">
                          ₹{formatCurrency(futureCorpus)}
                        </div>
                      </div>
                      <div
                        className={`rounded-lg p-3 flex flex-col ${
                          monthlySIP > 0 ? 'bg-red-100' : 'bg-gray-100'
                        }`}
                      >
                        <div
                          className={`text-xs mb-2 uppercase h-8 flex items-center ${
                            monthlySIP > 0
                              ? 'font-bold text-red-700'
                              : 'font-semibold text-gray-700'
                          }`}
                        >
                          Monthly SIP Required
                        </div>
                        <div
                          className={`text-lg font-bold ${
                            monthlySIP > 0 ? 'text-red-600' : 'text-gray-900'
                          }`}
                        >
                          ₹{formatCurrency(monthlySIP)}
                        </div>
                      </div>
                      <div className="bg-gray-100 rounded-lg p-3 flex flex-col">
                        <div className="text-xs font-semibold text-gray-700 mb-2 uppercase h-8 flex items-center">
                          Invested Principal
                        </div>
                        <div className="text-lg font-bold text-gray-900">
                          ₹{formatCurrency(totalInvested)}
                        </div>
                      </div>
                      <div className="bg-gray-100 rounded-lg p-3 flex flex-col">
                        <div className="text-xs font-semibold text-gray-700 mb-2 uppercase h-8 flex items-center">
                          Growth
                        </div>
                        <div className="text-lg font-bold text-gray-900">
                          ₹{formatCurrency(growth)}
                        </div>
                      </div>
                      <div className="bg-gray-100 rounded-lg p-3 flex flex-col">
                        <div className="text-xs font-semibold text-gray-700 mb-2 uppercase h-8 flex items-center">
                          Absolute Returns
                        </div>
                        <div className="text-lg font-bold text-gray-900">
                          {isNaN(absoluteReturns) || !isFinite(absoluteReturns)
                            ? '0'
                            : absoluteReturns.toFixed(0)}
                          %
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {goals.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg mb-2">No goals added yet</p>
              </div>
            )}

            {/* Total Summary */}
            {goals.length > 0 && (
              <div className="mt-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg p-4 shadow-lg pdf-total-summary">
                <div className="flex flex-col md:flex-row justify-between items-center gap-3">
                  <div>
                    <div className="text-sm font-semibold uppercase mb-1 opacity-90">
                      Total Monthly SIP Required
                    </div>
                    <div className="text-4xl font-bold">
                      ₹
                      {formatCurrency(
                        goals.reduce((total, goal) => {
                          const futureCorpus = calculateFutureCorpus(
                            goal.corpusRequired,
                            goal.inflationRate,
                            goal.timeline
                          );
                          const monthlySIP = calculateMonthlySIP(
                            futureCorpus,
                            goal.annualReturns,
                            goal.timeline
                          );
                          return (
                            total +
                            (isNaN(monthlySIP) || !isFinite(monthlySIP)
                              ? 0
                              : monthlySIP)
                          );
                        }, 0)
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm opacity-90 mb-1">
                      Total Future Corpus
                    </div>
                    <div className="text-2xl font-bold">
                      ₹
                      {formatCurrency(
                        goals.reduce((total, goal) => {
                          const futureCorpus = calculateFutureCorpus(
                            goal.corpusRequired,
                            goal.inflationRate,
                            goal.timeline
                          );
                          return (
                            total +
                            (isNaN(futureCorpus) || !isFinite(futureCorpus)
                              ? 0
                              : futureCorpus)
                          );
                        }, 0)
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Create a Plan Section */}
          {goals.length > 0 && (
            <div className="mt-8 no-export">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={createPlan}
                  onChange={e => handleCreatePlanToggle(e.target.checked)}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-lg font-semibold text-gray-900">
                  Create a plan
                </span>
              </label>
            </div>
          )}

          {/* Allocation Table */}
          {createPlan && (
            <div className="mt-6 overflow-x-auto pdf-plan-allocation-section">
              <table className="w-full border-collapse table-fixed">
                <colgroup>
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '40%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '5%' }} className="no-export" />
                </colgroup>
                <thead>
                  <tr className="bg-black text-white">
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Goal
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-left bg-yellow-300 text-black">
                      Required SIP
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Scheme Name
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-left bg-yellow-300 text-black">
                      Scheme Weightage
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-left bg-yellow-300 text-black">
                      Req. Scheme Amount
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-left bg-yellow-300 text-black">
                      Proposed Scheme SIP
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-center no-export">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {goals.map((goal, goalIndex) => {
                    const goalSchemes = schemeAllocations.filter(
                      s => s.goalId === goal.id
                    );
                    const futureCorpus = calculateFutureCorpus(
                      goal.corpusRequired,
                      goal.inflationRate,
                      goal.timeline
                    );
                    const requiredSIP = calculateMonthlySIP(
                      futureCorpus,
                      goal.annualReturns,
                      goal.timeline
                    );
                    const totalWeightage = getTotalWeightage(goal.id);
                    const remainingWeightage = 100 - totalWeightage;

                    return (
                      <>
                        {goalSchemes.map((scheme, schemeIndex) => (
                          <tr
                            key={scheme.id}
                            className={
                              goalIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                            }
                          >
                            {schemeIndex === 0 && (
                              <>
                                <td
                                  className="border border-gray-300 px-4 py-2 font-semibold break-words"
                                  rowSpan={goalSchemes.length + 1}
                                >
                                  {goal.name}
                                </td>
                                <td
                                  className="border border-gray-300 px-2 py-2 font-bold text-sm"
                                  rowSpan={goalSchemes.length + 1}
                                >
                                  {Math.round(requiredSIP)}
                                </td>
                              </>
                            )}
                            <td className="border border-gray-300 px-2 py-1">
                              <input
                                type="text"
                                value={scheme.schemeName}
                                onChange={e =>
                                  updateScheme(
                                    scheme.id,
                                    'schemeName',
                                    e.target.value
                                  )
                                }
                                className="w-full px-2 py-1 border-none bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded break-words"
                                placeholder="Enter scheme name"
                              />
                            </td>
                            <td className="border border-gray-300 px-2 py-1">
                              <input
                                type="number"
                                value={scheme.weightage}
                                onChange={e =>
                                  updateScheme(
                                    scheme.id,
                                    'weightage',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className="w-full px-2 py-1 border-none bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded text-sm"
                                min="0"
                                max="100"
                              />
                            </td>
                            <td className="border border-gray-300 px-2 py-2 text-sm">
                              {scheme.requiredAmount}
                            </td>
                            <td className="border border-gray-300 px-2 py-1">
                              <input
                                type="number"
                                value={scheme.proposedSIP}
                                onChange={e =>
                                  updateScheme(
                                    scheme.id,
                                    'proposedSIP',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className="w-full px-2 py-1 border-none bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded text-sm"
                              />
                            </td>
                            <td className="border border-gray-300 px-2 py-1 text-center no-export">
                              <button
                                onClick={() => removeScheme(scheme.id)}
                                className="text-red-500 hover:text-red-700 p-1"
                                title="Remove scheme"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {/* Add Scheme Row */}
                        <tr
                          className={
                            goalIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                          }
                        >
                          <td
                            colSpan={3}
                            className="border border-gray-300 px-2 py-2"
                          >
                            <button
                              onClick={() => addScheme(goal.id)}
                              disabled={remainingWeightage <= 0}
                              className={`text-sm flex items-center gap-1 ${
                                remainingWeightage <= 0
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-blue-600 hover:text-blue-800'
                              }`}
                            >
                              <Plus size={14} />
                              Add scheme
                            </button>
                          </td>
                          <td
                            colSpan={2}
                            className="border border-gray-300 px-2 py-2 text-right"
                          >
                            {remainingWeightage > 0 && (
                              <div className="text-xs text-gray-600 no-export">
                                Remaining: {remainingWeightage}%
                              </div>
                            )}
                          </td>
                        </tr>
                      </>
                    );
                  })}
                  {/* Total Row */}
                  <tr className="bg-black text-white font-bold">
                    <td
                      colSpan={2}
                      className="border border-gray-300 px-4 py-3 text-right"
                    >
                      {Math.round(
                        goals.reduce((sum, goal) => {
                          const goalSchemes = schemeAllocations.filter(
                            s => s.goalId === goal.id
                          );
                          if (goalSchemes.length === 0) return sum;
                          const futureCorpus = calculateFutureCorpus(
                            goal.corpusRequired,
                            goal.inflationRate,
                            goal.timeline
                          );
                          const requiredSIP = calculateMonthlySIP(
                            futureCorpus,
                            goal.annualReturns,
                            goal.timeline
                          );
                          return sum + requiredSIP;
                        }, 0)
                      )}
                    </td>
                    <td className="border border-gray-300 px-2 py-3"></td>
                    <td className="border border-gray-300 px-2 py-3"></td>
                    <td className="border border-gray-300 px-2 py-3 text-right">
                      {Math.round(
                        schemeAllocations.reduce(
                          (sum, scheme) => sum + scheme.requiredAmount,
                          0
                        )
                      )}
                    </td>
                    <td className="border border-gray-300 px-2 py-3 text-right">
                      {Math.round(
                        schemeAllocations.reduce(
                          (sum, scheme) => sum + scheme.proposedSIP,
                          0
                        )
                      )}
                    </td>
                    <td className="border border-gray-300 px-2 py-3 no-export"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoalPlanner;
